// packages/main/app/web/components-v2/UnifiedImageDialog/UploadVideoTab.tsx
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
	useVideoAttachmentFromSystemJob,
	useVideoUploadMutation,
} from '../../components/socialMedia/SocialMediaImageSearchAndDisplay/hooks';
import { CloudUploadIcon } from '../../components/svgs/icons/CloudUploadIcon';
import { Button } from '../Button';
import { s } from './styles';

interface UploadVideoTabProps {
	impersonationContext?: Api.IImpersonationContext;
	postId?: string;
	forUserId?: string;
	onVideoJobGenerated?: (jobId: string, videoElement: HTMLVideoElement) => void;
	onVideoAttachmentGenerated?: (attachment: Api.IFileAttachment, jobId: string) => void;
}

const ACCEPTED_FORMATS = ['video/mp4', 'video/quicktime'];

export function UploadVideoTab({
	onVideoJobGenerated,
	onVideoAttachmentGenerated,
	impersonationContext,
	postId,
	forUserId,
}: UploadVideoTabProps) {
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [uploadedVideo, setUploadedVideo] = useState<Api.IFileAttachment | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const videoElementRef = useRef<HTMLVideoElement | null>(null);
	const userSession = useUserSession();

	const videoUploadMutation = useVideoUploadMutation({
		onError: (err: Api.IOperationResultNoValue) => {
			setError(err?.systemMessage || 'Failed to upload video. Please try again.');
			toast.error('Failed to upload video');
		},
		onSuccess: (jobId: string) => {
			onVideoJobGenerated?.(jobId, videoElementRef.current);
			toast.success('Video uploaded successfully.');
		},
		skipConversion: true,
		impersonationContext,
	});

	const { videoAttachment, errorMessage, resetErrorMessage } = useVideoAttachmentFromSystemJob({
		jobId: videoUploadMutation.data,
		impersonationContext,
	});
	const isLoading =
		videoUploadMutation.isPending || (Boolean(videoUploadMutation.data) && !videoAttachment && !errorMessage);

	React.useEffect(() => {
		if (videoAttachment) {
			setUploadedVideo(videoAttachment);
			toast.success('Video processed successfully');
		}
	}, [videoAttachment]);

	React.useEffect(() => {
		if (errorMessage) {
			setError(errorMessage);
			toast.error(errorMessage);
			resetErrorMessage();
		}
	}, [errorMessage, resetErrorMessage]);

	const handleFile = (file: File) => {
		if (!file) {
			return;
		}

		setError(null);

		const mimeType = file.type?.toLocaleLowerCase();

		if (mimeType.startsWith('video/')) {
			// Handle video upload
			try {
				// Create a video element
				const video = document.createElement('video');
				video.preload = 'metadata';

				// Once video metadata is loaded
				video.onloadedmetadata = () => {
					const userId = forUserId || userSession.user.id;
					videoUploadMutation.mutate({
						duration: video.duration,
						file,
						forUserId: userId,
						height: video.videoHeight,
						postId,
						width: video.videoWidth,
					});
				};

				// Set the source of the video element
				video.src = URL.createObjectURL(file);

				videoElementRef.current = video;
			} catch {
				setError('Failed to load video. Please try another file.');
			}
		}

		// Clear the input field
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFile(e.dataTransfer.files[0]);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			handleFile(e.target.files[0]);
		}
	};

	const handleClick = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		fileInputRef.current?.click();
	};

	const handleAddVideo = () => {
		if (uploadedVideo) {
			onVideoAttachmentGenerated?.(uploadedVideo, videoUploadMutation.data);
		}
	};

	const handleChooseAnother = () => {
		setUploadedVideo(null);
		setError(null);
		if (videoElementRef.current) {
			URL.revokeObjectURL(videoElementRef.current.src);
			videoElementRef.current = null;
		}
	};

	// Show preview if video is uploaded
	if (uploadedVideo) {
		return (
			<div className={css(s.uploadContainer)}>
				<div className={css(s.previewSection)}>
					<div className={css(s.previewImageContainer)}>
						<video src={uploadedVideo.url} controls className={css(s.previewImage)} />
					</div>
					<div className={css(s.previewActions)}>
						<Button variant='secondary' onClick={handleChooseAnother}>
							Choose Another
						</Button>
						<Button variant='primary' onClick={handleAddVideo}>
							Add Video
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Show loading state during upload/processing
	if (isLoading) {
		return (
			<div className={css(s.uploadContainer)}>
				<div className={css(s.dropzone)}>
					<div className={css(s.dropzoneContent)}>
						<LoadingSpinner type='small' />
						<h3 className={css(s.dropzoneTitle)}>
							{videoUploadMutation.data ? 'Processing video...' : 'Uploading video...'}
						</h3>
						<p className={css(s.dropzoneDescription)}>
							{videoUploadMutation.data
								? 'Your video is being transcoded. This may take a few moments.'
								: 'Please wait while your video is uploaded.'}
						</p>
					</div>
				</div>
				{error && <div className={css(s.errorMessage)}>{error}</div>}
			</div>
		);
	}

	// Show dropzone if no video uploaded yet
	return (
		<div className={css(s.uploadContainer)}>
			<div
				className={css(s.dropzone)}
				onDragEnter={handleDrag}
				onDragLeave={handleDrag}
				onDragOver={handleDrag}
				onDrop={handleDrop}
				onClick={handleClick}
				data-drag-active={dragActive || undefined}
			>
				<div className={css(s.dropzoneContent)}>
					<CloudUploadIcon className={css(s.dropzoneIcon)} />
					<h3 className={css(s.dropzoneTitle)}>Drop video here or click to browse</h3>
					<p className={css(s.dropzoneDescription)}>Supports MP4, MOV</p>
					{!isLoading && (
						<Button variant='primary' onClick={handleClick} type='button'>
							Choose File
						</Button>
					)}
				</div>
				<input
					ref={fileInputRef}
					type='file'
					accept={ACCEPTED_FORMATS.join(',')}
					onChange={handleChange}
					className={css(s.fileInput)}
					style={{ display: 'none' }}
				/>
			</div>
			{error && <div className={css(s.errorMessage)}>{error}</div>}
		</div>
	);
}
